import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {
  getAnchorHotelId,
  getIsSearchCompleted,
  getOfferEntity
} from 'modules/sapiSearch/selectors'
import {getTopOffers} from 'modules/sapiSearch/utils'
import {getSerializedLandingQuery} from 'modules/search/selectors'

import {checkPriceChange} from '../utils/price'

interface useCheckPriceWatchMismatchProps {
  hotelId: string
  clickedOfferRate: number
}

type useCheckPriceWatchMismatchResponse = {
  isPriceDecrease: boolean
  isPriceIncrease: boolean
  priceDifference: number
  currentTopPrice: number
  percentageDifference: number
  hasMismatch: boolean
}

export const useCheckPriceWatchMismatch = ({
  hotelId,
  clickedOfferRate
}: useCheckPriceWatchMismatchProps): useCheckPriceWatchMismatchResponse => {
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const serializedLandingQuery = useSelector(getSerializedLandingQuery)
  const priceWatchId = serializedLandingQuery?.priceWatchId ?? undefined
  const anchorHotelId = useSelector(getAnchorHotelId)
  const isAnchor = anchorHotelId === hotelId
  const isSearchCompleted = useSelector(getIsSearchCompleted)

  return useMemo(() => {
    if (!priceWatchId || !isAnchor)
      return {
        isPriceDecrease: false,
        isPriceIncrease: false,
        priceDifference: 0,
        currentTopPrice: 0,
        percentageDifference: 0,
        hasMismatch: false
      }

    const topOffers = getTopOffers(offerEntity)

    const topOffer = topOffers?.sort((a, b) => a?.totalRate - b?.totalRate)?.[0]
      ?.totalRate

    const {
      isPriceIncrease,
      isPriceDecrease,
      priceDifference,
      percentageDifference
    } = checkPriceChange({
      clickedOfferRate,
      currentTopOfferRate: topOffer,
      priceChangeThreshold: 2
    })

    const hasMismatch =
      isSearchCompleted && (isPriceDecrease || isPriceIncrease)

    return {
      hasMismatch,
      isPriceDecrease,
      isPriceIncrease,
      priceDifference,
      percentageDifference,
      currentTopPrice: topOffer
    }
  }, [clickedOfferRate, offerEntity, priceWatchId, isAnchor, isSearchCompleted])
}
